import Image from 'next/image';
import Link from 'next/link';
import { TypographyLarge, TypographyTitle } from '@/components/base/Typography';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils';

export const PictureAndButton = ({
  imageSrc,
  imageAlt,
  title,
  subtitle,
  description,
  buttonText,
  buttonHref = '/',
  buttonVariant = 'default',
  orientation = 'left',
  background = 'dark',
  className,
  ...props
}) => {
  // Background style variations
  const backgroundStyles = {
    dark: 'bg-gradient-to-r from-slate-800 via-[#0f172b] to-slate-900',
    light: 'bg-gray-100',
    blue: 'bg-gradient-to-r from-blue-900 to-blue-700',
    teal: 'bg-gradient-to-r from-teal-800 via-teal-700 to-teal-800',
    gradient: 'bg-gradient-to-r from-slate-900 via-blue-900 to-slate-800',
    showcase: 'bg-gradient-to-r from-stone-100 to-white',
  };

  const isLight = background === 'light' || background === 'showcase';

  // Button style variations
  const buttonStyles = {
    default: 'bg-white text-gray-900 hover:bg-gray-100',
    outline: 'border-1 border-white bg-transparent text-white hover:bg-white hover:text-gray-900',
    blue: 'bg-blue-600 text-white hover:bg-blue-700',
    dark: 'bg-slate-900 text-white hover:bg-slate-800',
  };

  return (
    <div
      className={cn(
        'relative min-h-[50vh] overflow-hidden',
        backgroundStyles[background] || background,
        className,
      )}
      {...props}
    >
      {/* Image Section - Position based on orientation */}
      <div
        className={cn(
          'absolute top-0 h-full',
          orientation === 'left' ? 'right-0 w-2/3 lg:w-1/2' : 'left-0 w-2/3 lg:w-1/2',
        )}
      >
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          className={cn('object-cover object-center', background !== 'showcase' && 'opacity-80')}
          priority
        />
        {background !== 'showcase' && (
          <div
            className={cn(
              'absolute inset-0',
              orientation === 'left'
                ? 'bg-gradient-to-l from-transparent via-slate-200/20 to-gray-100'
                : 'bg-gradient-to-r from-transparent via-slate-900/20 to-slate-900',
            )}
          ></div>
        )}
      </div>

      {/* Content Section */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-full min-h-[20vh] items-center">
          {/* Content positioning based on orientation */}
          <div className={cn('max-w-3xl py-16 lg:py-24', orientation === 'right' ? 'ml-auto' : '')}>
            {/* Title & Subtitle */}
            <div className="mb-8">
              {title &&
                (typeof title === 'string' ? (
                  <TypographyTitle className={cn('mb-5', isLight ? 'text-gray-900' : 'text-white')}>
                    {title}
                  </TypographyTitle>
                ) : (
                  <div className={cn(isLight ? 'text-gray-900' : 'text-white')}>{title}</div>
                ))}

              {subtitle && (
                <div className="mb-8">
                  <TypographyLarge
                    className={cn(
                      'text-xl leading-relaxed lg:text-2xl',
                      isLight ? 'text-gray-700' : 'text-gray-200',
                    )}
                  >
                    {subtitle}
                  </TypographyLarge>
                </div>
              )}

              {description && (
                <TypographyLarge
                  className={cn(
                    'text-lg leading-relaxed sm:text-xl lg:text-2xl',
                    isLight ? 'text-gray-600' : 'text-white',
                  )}
                >
                  {description}
                </TypographyLarge>
              )}
            </div>

            {/* Button */}
            {buttonText && (
              <div className="flex">
                <Button
                  size="lg"
                  variant={buttonVariant === 'outline' ? 'outline' : 'default'}
                  className={cn(
                    'rounded-full px-12 py-4 text-lg font-medium tracking-wide shadow-lg transition-all duration-300 hover:shadow-xl',
                    buttonStyles[buttonVariant] || '',
                  )}
                  asChild
                >
                  <Link href={buttonHref}>{buttonText}</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
